import React, { useState } from "react";
import "../../devices/components/DeleteDevice.css";

// Confirmation for deleting a whole microgrid. The delete removes the
// microgrid's storage container, so the dialog names that container in full
// and the storage account it is in, and the Delete button stays disabled
// until the user types the microgrid's name exactly as the list shows it.
const DeleteMicrogridModal = ({
  open,
  microgrid,
  storageAccountName,
  onConfirm,
  onClose,
  loading = false,
}) => {
  const [typedName, setTypedName] = useState("");

  // Clear the typed name whenever the modal opens for another microgrid (or
  // closes), so a match never carries over from an earlier confirmation.
  const [prevMicrogrid, setPrevMicrogrid] = useState(microgrid);
  if (microgrid !== prevMicrogrid) {
    setPrevMicrogrid(microgrid);
    setTypedName("");
  }

  if (!open || !microgrid) return null;

  const expectedName = microgrid.displayName || microgrid.name;
  const nameMatches = typedName.trim() === expectedName;
  const deleteDisabled = loading || !nameMatches;

  return (
    <>
      <div
        className="delete-device-overlay"
        onClick={loading ? undefined : onClose}
      />
      <div
        className="delete-device-popup-container"
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-microgrid-title"
      >
        <div className="delete-device-header-container">
          <h3 className="delete-device-title" id="delete-microgrid-title">
            Delete microgrid &ldquo;{expectedName}&rdquo;?
          </h3>
          <button
            className="delete-device-close-button"
            onClick={onClose}
            disabled={loading}
            aria-label="Close"
          >
            &times;
          </button>
        </div>
        <div className="delete-device-content-container">
          <p className="delete-device-warning">
            This action cannot be undone. The microgrid&rsquo;s inputs, its
            sysconfig, and the results of every simulation run will be
            permanently removed.
          </p>
          <dl
            style={{
              display: "grid",
              gridTemplateColumns: "auto 1fr",
              gap: "4px 12px",
              margin: "0 0 16px",
              fontSize: 14,
              wordBreak: "break-all",
            }}
          >
            <dt style={{ fontWeight: 500 }}>Container</dt>
            <dd style={{ margin: 0 }}>
              <code>{microgrid.name}</code>
            </dd>
            <dt style={{ fontWeight: 500 }}>Storage account</dt>
            <dd style={{ margin: 0 }}>
              <code>{storageAccountName || "not known"}</code>
            </dd>
          </dl>
          <label
            htmlFor="delete-microgrid-confirm-input"
            style={{ display: "block", marginBottom: 4, fontWeight: 500 }}
          >
            Type <strong>{expectedName}</strong> to confirm
          </label>
          <input
            id="delete-microgrid-confirm-input"
            type="text"
            value={typedName}
            onChange={(e) => setTypedName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !deleteDisabled) {
                e.preventDefault();
                onConfirm();
              }
            }}
            disabled={loading}
            autoComplete="off"
            autoFocus
            style={{
              display: "block",
              width: "100%",
              padding: "8px 10px",
              marginBottom: 24,
              fontSize: 14,
              border: "1px solid #cbd5e1",
              borderRadius: 6,
              outline: "none",
              boxSizing: "border-box",
            }}
          />
        </div>
        <div className="delete-device-form-actions">
          <button
            className="delete-device-cancel-action"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            className="delete-device-delete-button"
            onClick={onConfirm}
            disabled={deleteDisabled}
          >
            {loading ? (
              <>
                <span className="spinner-small" /> Deleting&hellip;
              </>
            ) : (
              "Delete microgrid"
            )}
          </button>
        </div>
      </div>
    </>
  );
};

export default DeleteMicrogridModal;
